import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { landxmlExportTrackingAtom } from '@/atoms/landxmlExport-progress.atom';
import { execute } from '@/graphql/execute';
import { startLandxmlExport } from '@/queries/landxmlExport.query';
import { useDialog } from '@/providers/DialogProvider';
import { getFragments } from '@/components/IFC2';
import { resolveGuids } from '@/components/fragmentTreeData';
import {
  buildGroups,
  candidateProperties,
  emptyScan,
  propertyValues,
  scanPropertySets,
  type PropertyScan,
} from '@/components/fragmentPropertyGroups';
import LandxmlExportTree from '@/components/LandxmlExportTree';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Props {
  projectId: string;
  layerId: string;
  layerName: string;
  /**
   * localIds to pre-tick in the element tree (the subtree picked in the IFC
   * drawer's context menu). Empty/undefined ⇒ whole model, subject to the
   * class filter. The user can refine the selection inside the dialog.
   */
  initialLocalIds?: number[];
}

/** How long to wait after the last tick before resolving GUIDs. */
const RESOLVE_DEBOUNCE_MS = 300;

/**
 * Must match MAX_GROUPS in `landxmlExport.mutation.ts`: a property with more
 * distinct values than this is an identifier, not a grouping, and the server
 * would reject the fan-out anyway (one Fargate task per group).
 */
const MAX_GROUPS = 20;

/** Sentinel for the "don't group" option — Radix Select rejects an empty value. */
const NO_GROUPING = '__none__';

const parseClassText = (text: string) =>
  text
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => /^ifc[a-z0-9]+$/i.test(s));

/**
 * Starts an IFC → LandXML surface export. Elements can be scoped with the
 * in-dialog spatial tree; classes are listed from the loaded fragment model
 * when available (same source as the "color by class" layer setting),
 * otherwise the user types them.
 */
/** DialogContent class for this dialog: cap at 90vh and let the body scroll (header/footer stay pinned). */
export const LANDXML_EXPORT_DIALOG_CLASS = 'max-h-[90vh] flex flex-col';

export default function LandxmlExportDialog({ projectId, layerId, layerName, initialLocalIds }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const [name, setName] = useState(() => t('landxmlExport.defaultName', { name: layerName }));
  const [classes, setClasses] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [classText, setClassText] = useState('');
  const setTrackers = useSetAtom(landxmlExportTrackingAtom);

  // Property-set grouping (see `fragmentPropertyGroups`): the escape hatch for
  // models whose spatial tree is one flat bucket of unnamed proxies. Each
  // ticked value becomes its own export job, LandXML file and layer.
  const [scan, setScan] = useState<PropertyScan>(emptyScan);
  const [scanning, setScanning] = useState(true);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [groupKey, setGroupKey] = useState<string>(NO_GROUPING);
  const [groupValues, setGroupValues] = useState<Set<string>>(() => new Set());
  // One welded surface per export instead of one per element. On by default
  // for a grouped export: a pavement course split across 141 solids is only
  // useful downstream (surface difference, Civil 3D) as a single TIN.
  const [merge, setMerge] = useState(true);

  // Ticked localIds (containers included). Empty ⇒ the whole model.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(initialLocalIds ?? []));
  const [resolved, setResolved] = useState<{ guids: string[]; categories: Set<string> } | undefined>(undefined);
  const [resolving, setResolving] = useState((initialLocalIds?.length ?? 0) > 0);
  // Set when the GUID scan (or the model lookup) failed. Critical: without a
  // resolved GUID list a scoped export would silently fall back to `guids: null`
  // — i.e. export the WHOLE model — so this hard-blocks the Export button.
  const [resolveError, setResolveError] = useState(false);

  // Groups the user has actually ticked. Non-empty ⇒ a grouped export: the
  // groups ARE the selection, so the element tree is ignored (and disabled).
  const groupOptions = useMemo(
    () => (groupKey === NO_GROUPING ? [] : propertyValues(scan, groupKey)),
    [scan, groupKey],
  );
  const groups = useMemo(
    () => (groupKey === NO_GROUPING ? [] : buildGroups(scan, groupKey, groupValues)),
    [scan, groupKey, groupValues],
  );
  const grouping = groups.length > 0;

  const usingSelection = !grouping && selectedIds.size > 0;

  // Resolving GUIDs is a chunked attribute fetch over the whole selection, so
  // it is debounced — ticking a few boxes in a row shouldn't fire one scan per
  // click. `_category` rides along in the same payload and narrows the class list.
  useEffect(() => {
    if (selectedIds.size === 0) {
      setResolved(undefined);
      setResolveError(false);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    setResolveError(false);
    const handle = setTimeout(() => {
      (async () => {
        try {
          const fragments = await getFragments();
          const model = fragments.models.list.get(`frag-${layerId}`);
          if (!model) throw new Error(`fragment model frag-${layerId} is not loaded`);
          const result = await resolveGuids(model, [...selectedIds]);
          if (cancelled) return;
          setResolved(result);
          setResolving(false);
        } catch (err) {
          if (cancelled) return;
          console.warn('[LandxmlExportDialog] GUID resolve failed', err);
          setResolved(undefined);
          setResolveError(true);
          setResolving(false);
        }
      })();
    }, RESOLVE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [selectedIds, layerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fragments = await getFragments();
        const model = fragments.models.list.get(`frag-${layerId}`);
        if (!model) return;
        const cats = await model.getCategories();
        if (!cancelled) setClasses([...cats].sort());
      } catch {
        // legacy IFC layer or model not loaded → free-text fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [layerId]);

  // One pass over every element's property sets, up front: it is what makes
  // the "group by" picker possible at all, and on a flat model it is the only
  // way to express a selection. Failures are non-fatal — the picker just has
  // nothing to offer and the tree / class filter still work.
  useEffect(() => {
    let cancelled = false;
    setScanning(true);
    (async () => {
      try {
        const fragments = await getFragments();
        const model = fragments.models.list.get(`frag-${layerId}`);
        if (!model) return;
        const result = await scanPropertySets(model, {
          isCancelled: () => cancelled,
          onProgress: (done, total) => {
            if (!cancelled) setScanProgress({ done, total });
          },
        });
        if (!cancelled) setScan(result);
      } catch (err) {
        console.warn('[LandxmlExportDialog] property scan failed', err);
      } finally {
        if (!cancelled) setScanning(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [layerId]);

  // With a selection, only the classes actually present in it are worth
  // offering — the model-wide list is mostly noise there.
  const visibleClasses = useMemo(() => {
    if (!usingSelection || !resolved) return classes;
    const narrowed = classes.filter((c) => resolved.categories.has(c.toUpperCase()));
    // Defensive: if nothing matched (attribute shapes differ across model
    // versions) fall back to the full list rather than an empty box.
    return narrowed.length > 0 ? narrowed : classes;
  }, [classes, usingSelection, resolved]);

  const candidates = useMemo(() => candidateProperties(scan, MAX_GROUPS), [scan]);

  const ifcTypes = useMemo(
    () => (visibleClasses.length > 0 ? visibleClasses.filter((c) => selected.has(c)) : parseClassText(classText)),
    [visibleClasses, selected, classText],
  );

  // While resolving, `resolved` still describes the PREVIOUS selection, so the
  // tick count (an upper bound — containers included) is the honest estimate.
  const selectedCount = resolving ? selectedIds.size : resolved?.guids.length ?? selectedIds.size;
  const emptySelection = usingSelection && !resolving && resolved !== undefined && resolved.guids.length === 0;
  // A scoped export is only safe with a resolved, non-empty GUID list: blocked
  // while the scan is in flight, when it failed, and when it came back empty.
  // A chosen property with nothing ticked is NOT "no grouping": falling
  // through would quietly export the whole model as one file, which is the
  // opposite of what the user just asked for.
  const emptyGrouping = groupKey !== NO_GROUPING && groups.length === 0;
  const scopeBlocked = grouping
    ? false
    : emptyGrouping || resolving || emptySelection || (usingSelection && (resolveError || resolved === undefined));

  const mutation = useMutation({
    mutationFn: async () => {
      // Belt-and-braces: `guids: null` means "whole model" server-side, so it
      // must never be sent while the user has a selection. A grouped export
      // carries its own GUIDs per group and never goes through the tree.
      if (usingSelection && !(resolved && resolved.guids.length > 0)) {
        throw new Error(t('landxmlExport.resolveFailed'));
      }
      return execute(startLandxmlExport, {
        input: {
          layerId,
          guids: !grouping && usingSelection ? resolved!.guids : null,
          groups: grouping ? groups.map((g) => ({ name: g.value, guids: g.guids })) : null,
          merge,
          ifcTypes: ifcTypes.length > 0 ? ifcTypes : null,
          name: name.trim(),
        },
      });
    },
    onSuccess: (data) => {
      const batchId = data.startLandxmlExport;
      const expectedLayers = grouping ? groups.length : 1;
      const toastId = toast.loading(
        grouping
          ? t('landxmlExport.runningToastGroups', { name: layerName, count: expectedLayers })
          : t('landxmlExport.runningToast', { name: layerName }),
        { duration: Infinity },
      );
      setTrackers((prev) => [
        ...prev,
        { toastId, batchId, projectId, sourceLayerId: layerId, sourceLayerName: layerName, expectedLayers },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => toast.error(err.message || t('landxmlExport.failedToStart')),
  });

  const toggleGroupValue = (value: string, on: boolean) =>
    setGroupValues((prev) => {
      const next = new Set(prev);
      if (on) next.add(value);
      else next.delete(value);
      return next;
    });

  /**
   * Picking a property ticks every one of its values: "split this model into
   * its courses" is the reason the picker exists, and unticking the odd one
   * out is less work than ticking nine.
   */
  const chooseGroupKey = (key: string) => {
    setGroupKey(key);
    setGroupValues(key === NO_GROUPING ? new Set() : new Set(propertyValues(scan, key).map((v) => v.value)));
  };

  const toggleClass = (cls: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(cls);
      else next.delete(cls);
      return next;
    });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('landxmlExport.dialogTitle')}</DialogTitle>
      </DialogHeader>

      <div className='min-w-0 min-h-0 flex-1 space-y-4 overflow-y-auto py-2 pr-1'>
        <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
          {resolving && <Loader2 className='size-3 animate-spin' />}
          {grouping
            ? t('landxmlExport.scopeGroups', {
                count: groups.length,
                elements: groups.reduce((n, g) => n + g.guids.length, 0),
              })
            : usingSelection
              ? t('landxmlExport.scopeSelected', { count: selectedCount })
              : t('landxmlExport.scopeAll', { name: layerName })}
        </p>
        {emptySelection && <p className='text-xs text-destructive'>{t('landxmlExport.noElements')}</p>}
        {usingSelection && resolveError && (
          <p className='text-xs text-destructive'>{t('landxmlExport.resolveFailed')}</p>
        )}

        <div className='space-y-2'>
          <Label>{t('landxmlExport.groupByLabel')}</Label>
          {scanning ? (
            <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <Loader2 className='size-3 animate-spin' />
              {t('landxmlExport.groupScanning', { done: scanProgress.done, total: scanProgress.total })}
            </p>
          ) : candidates.length === 0 ? (
            <p className='text-xs text-muted-foreground'>{t('landxmlExport.groupNoProperties')}</p>
          ) : (
            <>
              <Select value={groupKey} onValueChange={chooseGroupKey} disabled={mutation.isPending}>
                <SelectTrigger data-testid='landxml-group-select'>
                  <SelectValue placeholder={t('landxmlExport.groupByPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_GROUPING}>{t('landxmlExport.groupByNone')}</SelectItem>
                  {candidates.map((c) => (
                    <SelectItem key={c.key} value={c.key} data-testid={`landxml-group-option-${c.key}`}>
                      {c.key} · {t('landxmlExport.groupValueCount', { count: c.distinct })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {groupOptions.length > 0 && (
                <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40 max-h-56 overflow-y-auto'>
                  {groupOptions.map((v) => (
                    <label
                      key={v.value}
                      className='flex items-center gap-2 border-b last:border-b-0 border-slate-200/70 px-3 py-1.5 text-xs dark:border-stone-700/40 cursor-pointer'>
                      <Checkbox
                        data-testid={`landxml-group-value-${v.value}`}
                        checked={groupValues.has(v.value)}
                        onCheckedChange={(c) => toggleGroupValue(v.value, c === true)}
                        disabled={mutation.isPending}
                      />
                      <span className='min-w-0 flex-1 truncate font-mono'>{v.value}</span>
                      <span className='shrink-0 text-muted-foreground'>{v.count}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className={emptyGrouping ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                {grouping
                  ? t('landxmlExport.groupHintActive', { count: groups.length })
                  : emptyGrouping
                    ? t('landxmlExport.groupNoneTicked')
                    : t('landxmlExport.groupHint')}
              </p>
            </>
          )}
        </div>

        <label className='flex items-start gap-2 cursor-pointer'>
          <Checkbox
            data-testid='landxml-merge'
            checked={merge}
            onCheckedChange={(c) => setMerge(c === true)}
            disabled={mutation.isPending}
            className='mt-0.5'
          />
          <span className='space-y-0.5'>
            <span className='block text-sm'>{t('landxmlExport.mergeLabel')}</span>
            <span className='block text-xs text-muted-foreground'>{t('landxmlExport.mergeHint')}</span>
          </span>
        </label>

        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-2'>
            <Label>{t('landxmlExport.selectionLabel')}</Label>
            {usingSelection && (
              <Button
                variant='link'
                type='button'
                className='h-auto p-0 text-xs'
                onClick={() => setSelectedIds(new Set())}
                disabled={mutation.isPending}>
                {t('landxmlExport.clearSelection')}
              </Button>
            )}
          </div>
          <LandxmlExportTree
            layerId={layerId}
            selected={selectedIds}
            onChange={setSelectedIds}
            disabled={mutation.isPending || grouping}
          />
          <p className='text-xs text-muted-foreground'>
            {grouping ? t('landxmlExport.selectionHintGrouped') : t('landxmlExport.selectionHint')}
          </p>
        </div>

        <div className='space-y-2'>
          <Label>{t('landxmlExport.classesLabel')}</Label>
          {visibleClasses.length > 0 ? (
            <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40 max-h-56 overflow-y-auto'>
              {visibleClasses.map((cls) => (
                <label
                  key={cls}
                  className='flex items-center gap-2 border-b last:border-b-0 border-slate-200/70 px-3 py-1.5 text-xs font-mono dark:border-stone-700/40 cursor-pointer'>
                  <Checkbox
                    checked={selected.has(cls)}
                    onCheckedChange={(v) => toggleClass(cls, v === true)}
                    disabled={mutation.isPending}
                  />
                  {cls}
                </label>
              ))}
            </div>
          ) : (
            <Input
              value={classText}
              onChange={(e) => setClassText(e.target.value)}
              placeholder={t('landxmlExport.classesPlaceholder')}
              disabled={mutation.isPending}
            />
          )}
          <p className='text-xs text-muted-foreground'>
            {usingSelection ? t('landxmlExport.classesHintSelection') : t('landxmlExport.classesHint')}
          </p>
        </div>

        <div className='space-y-2'>
          <Label>{t('landxmlExport.nameLabel')}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={mutation.isPending} />
          {grouping && <p className='text-xs text-muted-foreground'>{t('landxmlExport.nameHintGrouped')}</p>}
        </div>

        <p className='text-xs text-muted-foreground'>{t('landxmlExport.infoNote')}</p>
      </div>

      <DialogFooter>
        <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
          {t('landxmlExport.cancel')}
        </Button>
        <Button
          type='button'
          data-testid='landxml-export'
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || name.trim() === '' || scopeBlocked}>
          {t('landxmlExport.export')}
        </Button>
      </DialogFooter>
    </>
  );
}
